import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Inbox,
  Mail,
  Phone,
  Calendar,
  Eye,
  Tag,
} from "lucide-react";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "responded", label: "Responded" },
  { value: "closed", label: "Closed" },
];

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  responded: "bg-purple-100 text-purple-700",
  closed: "bg-green-100 text-green-700",
};

const StatusBadge = ({ status }) => (
  <Badge
    className={`${STATUS_STYLES[status] || "bg-gray-100 text-gray-700"} font-medium capitalize`}
  >
    {(status || "new").replace("_", " ")}
  </Badge>
);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
};

export const EnquiriesPanel = ({ enquiries, onChanged }) => {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  const counts = useMemo(() => {
    const c = { all: enquiries.length };
    for (const opt of STATUS_OPTIONS) {
      c[opt.value] = enquiries.filter((q) => q.status === opt.value).length;
    }
    return c;
  }, [enquiries]);

  const filtered = useMemo(() => {
    if (filter === "all") return enquiries;
    return enquiries.filter((q) => q.status === filter);
  }, [enquiries, filter]);

  const openDetail = (enquiry) => {
    setSelected(enquiry);
    setNotes(enquiry.admin_notes || "");
  };

  const updateEnquiry = async (enquiryId, newStatus, adminNotes) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/enquiries/${enquiryId}`, {
        status: newStatus,
        admin_notes: adminNotes,
      });
      toast.success(`Enquiry moved to "${newStatus.replace("_", " ")}"`);
      onChanged();
      setSelected(null);
    } catch (e) {
      logError("update enquiry failed", e);
      toast.error("Failed to update enquiry");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div data-testid="enquiries-panel">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[{ value: "all", label: "All" }, ...STATUS_OPTIONS].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setFilter(opt.value)}
            className={`p-4 rounded-xl text-left border transition-all ${
              filter === opt.value
                ? "border-[#2D5016] bg-[#E8F5E9]"
                : "border-[#E2E8E0] bg-white hover:border-[#7CB342]"
            }`}
            data-testid={`enquiry-filter-${opt.value}`}
          >
            <p className="text-xs text-[#5C6B57] uppercase tracking-wider font-semibold">
              {opt.label}
            </p>
            <p className="text-2xl font-bold text-[#1A2E12] mt-1">
              {counts[opt.value] || 0}
            </p>
          </button>
        ))}
      </div>

      <Card className="bg-white border-[#E2E8E0]">
        <CardHeader>
          <CardTitle className="text-[#1A2E12] flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#2D5016]" />
            Email Enquiries ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-[#5C6B57]">
              <Inbox className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No enquiries in this view.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((q) => (
                <div
                  key={q.id}
                  className="border border-[#E2E8E0] rounded-xl p-4 hover:shadow-md transition-shadow"
                  data-testid={`enquiry-row-${q.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="font-semibold text-[#1A2E12]">
                          {q.name}
                        </h3>
                        <StatusBadge status={q.status} />
                        {q.subject && (
                          <Badge className="bg-[#E8F5E9] text-[#2D5016] max-w-xs truncate">
                            {q.subject}
                          </Badge>
                        )}
                        {q.source && (
                          <span className="text-xs text-[#5C6B57] flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            {q.source}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#5C6B57]">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {q.email}
                        </span>
                        {q.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {q.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(q.created_at)}
                        </span>
                      </div>
                      {q.message && (
                        <p className="text-sm text-[#1A2E12] mt-2 line-clamp-2">
                          {q.message}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={q.status}
                        onValueChange={(v) =>
                          updateEnquiry(q.id, v, q.admin_notes || null)
                        }
                      >
                        <SelectTrigger
                          className="w-[140px] h-9"
                          data-testid={`enquiry-status-${q.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetail(q)}
                        className="border-[#2D5016] text-[#2D5016]"
                        data-testid={`enquiry-view-${q.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      {selected && (
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1A2E12] flex items-center gap-2">
                <Inbox className="w-5 h-5" />
                {selected.name} — {selected.subject || "Enquiry"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Email</p>
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-[#2D5016] hover:underline"
                  >
                    {selected.email}
                  </a>
                </div>
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Phone</p>
                  {selected.phone ? (
                    <a
                      href={`tel:${selected.phone}`}
                      className="text-[#2D5016] hover:underline"
                    >
                      {selected.phone}
                    </a>
                  ) : (
                    <p className="text-[#1A2E12]">—</p>
                  )}
                </div>
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Source</p>
                  <p className="text-[#1A2E12]">{selected.source || "—"}</p>
                </div>
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Postcode</p>
                  <p className="text-[#1A2E12]">{selected.postcode || "—"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Received</p>
                  <p className="text-[#1A2E12]">{formatDate(selected.created_at)}</p>
                </div>
              </div>

              {selected.message && (
                <div className="bg-[#F9FAF9] rounded-xl p-4">
                  <p className="text-xs text-[#5C6B57] uppercase tracking-wider mb-1">
                    Customer message
                  </p>
                  <p className="text-sm text-[#1A2E12] whitespace-pre-wrap">
                    {selected.message}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs text-[#5C6B57] uppercase tracking-wider mb-2">
                  Admin notes
                </p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Internal notes — e.g. action taken, follow-up date..."
                  data-testid="enquiry-admin-notes"
                />
              </div>

              {selected.updated_by && (
                <p className="text-xs text-[#5C6B57]">
                  Last updated by {selected.updated_by} on{" "}
                  {formatDate(selected.updated_at)}
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E2E8E0]">
                {STATUS_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={selected.status === opt.value ? "default" : "outline"}
                    disabled={updating}
                    onClick={() => updateEnquiry(selected.id, opt.value, notes)}
                    className={
                      selected.status === opt.value
                        ? "bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                        : "border-[#2D5016] text-[#2D5016]"
                    }
                    data-testid={`enquiry-set-${opt.value}`}
                  >
                    Mark as {opt.label}
                  </Button>
                ))}
              </div>

              <Button
                onClick={() =>
                  updateEnquiry(selected.id, selected.status, notes)
                }
                disabled={updating}
                className="w-full bg-[#7CB342] hover:bg-[#689F38] text-white rounded-full"
                data-testid="enquiry-save-notes"
              >
                Save notes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EnquiriesPanel;
